import React, { Component } from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';

import storeLoader from 'app/stores/storeLoader';
import { Field, FormDialog, InputGroup, Select } from 'core/form';
import { Box, Button, Card, Dialog, Flex, Spinner, Suspense, Tag, Text, Tooltip } from 'core/components';
import ToggleCollapse from 'app/components/ToggleCollapse';
import MitigationCollection from 'app/stores/mitigations/MitigationCollection';
import AdminTable from 'app/components/admin/AdminTable';
import Swatch from 'app/components/admin/Swatch';

import { afTemplateInfersIpCidr } from 'app/stores/mitigations/mitigationsUtils';
import { fields, options } from 'app/forms/config/manualMitigation';
import FlowspecMethodSummary from 'app/views/alerting/policies/PolicyDetails/FlowspecMethodSummary';
import { MITIGATION_API_STATES_LIST, MITIGATION_TYPES, AF_INFER } from '@kentik/ui-shared/mitigations/constants';

const PlatformMethodText = (props) => {
  const { option, mt } = props;
  const { method, platform } = option;
  const platformLabel = platform.get('name');
  const methodLabel = method.get('name');

  return (
    <Box>
      <Text as="div">{methodLabel}</Text>
      <Text small muted as="div" mt={mt}>
        Platform: {platformLabel}
      </Text>
    </Box>
  );
};

@storeLoader('$mitigations.methodsCollection', '$mitigations.platformsCollection', '$mkp.tenants')
@observer
export default class ManualMitigationFormDialog extends Component {
  state = {
    model: undefined,
    mitigationDetailsDialogOpen: false,
    collection: new MitigationCollection(),
    target: null,
    selectedMethod: null
  };

  componentDidMount() {
    const { $mitigations, initValues } = this.props;
    const model = $mitigations.newManualMitigationModel();

    if (initValues && typeof initValues === 'object') {
      model.set(initValues);
    }

    this.setState({ model });
  }

  get columns() {
    const { $mitigations } = this.props;
    return [
      {
        name: 'status',
        label: 'Status',
        computed: true,
        renderer: ({ model, value }) => (
          <Flex alignItems="center" gap={1}>
            <Swatch round ml={0} mt={0} bg={$mitigations.getMitigationStatusColor(value)} />
            <Text>{model.displayState}</Text>
          </Flex>
        )
      },
      {
        name: 'id',
        label: 'ID'
      },
      {
        name: 'target',
        label: 'Target'
      }
    ];
  }

  filterCollectionFn = (model) => {
    const { selectedMethod, target } = this.state;
    const matchesMethod = selectedMethod && model.get('methodID') === selectedMethod.method.id;
    const matchesTarget = target === '' ? false : model.get('target').includes(target);

    // If no method provided, match against target
    if (!selectedMethod) {
      return matchesTarget;
    }

    // If no target provided, match against method
    if (!target) {
      return matchesMethod;
    }

    // If both provided, require match for both
    return matchesMethod && matchesTarget;
  };

  filterCollection = () => {
    const { collection } = this.state;

    if (collection.hasFetched && !collection.loading) {
      collection.filter(this.filterCollectionFn);
    } else {
      collection
        .setServerFilter({
          states: MITIGATION_API_STATES_LIST.filter((state) => state !== 'archived'),
          showTenants: true,
          lookback: 60 * 60 * 24 * 90
        })
        .then(() => collection.filter(this.filterCollectionFn));
    }
  };

  tetherOptions = {
    offset: '-3px 0',
    constraints: [{ attachment: 'together', pin: true, to: 'window' }]
  };

  @action
  onAlertKeyChange = (field, value) => {
    this.setState({ target: value }, () => this.filterCollection());
  };

  @action
  onPlatformChange = (field, value) => {
    const { initValues } = this.props;
    const alertKeyField = field.form.getField('alertKey');
    const option = field.options.find((o) => o.value === value);
    const notApplicable = 'N/A';

    if (!option?.method) {
      return;
    }

    const isFlowspecWithNoIpInferred =
      option.method.type === MITIGATION_TYPES.FLOWSPEC &&
      !option.method.details.destIpCidr?.infer &&
      !option.method.details.sourceIpCidr?.infer;

    const isAfWithNoIpInferred =
      option.method.type === MITIGATION_TYPES.ADAPTIVE_FLOWSPEC &&
      !afTemplateInfersIpCidr((option.method.get(MITIGATION_TYPES.ADAPTIVE_FLOWSPEC) || {}).template);

    const neitherSourceNorDestInferred = isFlowspecWithNoIpInferred || isAfWithNoIpInferred;

    if (neitherSourceNorDestInferred) {
      alertKeyField.helpText = isAfWithNoIpInferred
        ? 'IP/CIDRs have been configured in the mitigation method template.'
        : 'Flowspec IP/CIDRs have been configured in the mitigation method.';
      alertKeyField.setRules('');
      alertKeyField.setValue(notApplicable);
      alertKeyField.disable();
    } else {
      alertKeyField.helpText = '';
      alertKeyField.setRules(alertKeyField.defaultRules);
      alertKeyField.enable();
      if (alertKeyField.getValue() === notApplicable) {
        alertKeyField.setValue(initValues?.alertKey || '');
      }
    }

    this.setState({ selectedMethod: option, target: '' }, () => this.filterCollection());
  };

  platformMethodOptionFormatter = (model) => {
    const { disabled } = model;

    return (
      <Flex justifyContent="space-between">
        <PlatformMethodText option={model} mt="-2px" />
        {disabled && (
          <Tag ml={1} small>
            Provisioning
          </Tag>
        )}
      </Flex>
    );
  };

  platformMethodValueFormatter = (option, placeholder) => {
    if (!option) {
      return <Text muted>{placeholder || 'Select a mitigation method...'}</Text>;
    }

    return <PlatformMethodText option={option} mt="2px" />;
  };

  tenantOptionFormatter = ({ label, value }) => (
    <Text>
      {label} {value && <Text muted>({value})</Text>}
    </Text>
  );

  tenantValueFormatter = (option, placeholder) => {
    if (!option?.value) {
      return <Text muted>{placeholder}</Text>;
    }

    const { label, value } = option;

    return (
      <Text>
        {label} {value && <Text muted>({value})</Text>}
      </Text>
    );
  };

  handleSubmit = (form, values) => {
    const { onSaved, onClose } = this.props;
    const { model } = this.state;

    model.set(values);
    return model.save().then(onSaved || onClose);
  };

  toggleMitigationDetailsDialog = () => {
    this.setState((prevState) => ({
      mitigationDetailsDialogOpen: !prevState.mitigationDetailsDialogOpen
    }));
  };

  get inferredMitigationMethodFields() {
    const { selectedMethod } = this.state;

    if (selectedMethod?.method?.type === MITIGATION_TYPES.FLOWSPEC) {
      const flowspecFields = selectedMethod.method.get(MITIGATION_TYPES.FLOWSPEC);
      const inferred = [];

      Object.keys(flowspecFields).forEach((fieldKey) => {
        if (flowspecFields[fieldKey]?.enabled && flowspecFields[fieldKey]?.infer) {
          inferred.push(fieldKey);
        }
      });

      return inferred;
    }

    if (selectedMethod?.method?.type === MITIGATION_TYPES.ADAPTIVE_FLOWSPEC) {
      const afDetails = selectedMethod.method.get(MITIGATION_TYPES.ADAPTIVE_FLOWSPEC) || {};
      const template = afDetails.template || {};
      const matches = template.matches || {};

      return Object.keys(matches).filter((matchKey) => {
        const field = matches[matchKey] || {};
        const effectiveInfer = field.infer === AF_INFER.UNSPECIFIED ? template.infer : field.infer;

        return field.enabled && effectiveInfer === AF_INFER.ENABLED;
      });
    }

    return [];
  }

  render() {
    const { $mitigations, $mkp, onClose, isOpen, kebabActions } = this.props;
    const { model, collection, selectedMethod, mitigationDetailsDialogOpen } = this.state;
    const loading = $mitigations.platformsCollection.loading || $mitigations.methodsCollection.loading;
    const inferredFields = this.inferredMitigationMethodFields;

    return (
      <FormDialog
        isOpen={model && isOpen}
        model={model}
        fields={fields}
        options={options}
        onClose={onClose}
        title="Start Manual Mitigation"
        entityName="Manual Mitigation"
        formComponentProps={{ large: true }}
        formActionsProps={{
          onSubmit: this.handleSubmit,
          onCancel: onClose,
          submitButtonProps: { disabled: collection.size === 1 }
        }}
        style={{ width: 600, height: 650 }}
        bodyStyle={{ padding: 0 }}
      >
        <Flex flexDirection="column">
          <Suspense loading={loading} fallback={<Spinner size={24} />}>
            <Flex gap={1}>
              <Box flex={1}>
                <Field
                  mb={0}
                  options={$mitigations.manualMitigationOptions}
                  name="pairingID"
                  onChange={this.onPlatformChange}
                >
                  <Select
                    fill
                    showFilter
                    tetherOptions={this.tetherOptions}
                    optionFormatter={this.platformMethodOptionFormatter}
                    valueRenderer={this.platformMethodValueFormatter}
                    rightElement={<Tag>Hey</Tag>}
                  />
                </Field>
              </Box>
              {/* For flowspec mitigation methods, we show additional details */}
              {selectedMethod?.platform?.get('type') === MITIGATION_TYPES.FLOWSPEC && (
                <Box mt="18px">
                  <Tooltip content="View Flowspec Method Details">
                    <Button icon="zoom-in" large minimal onClick={this.toggleMitigationDetailsDialog} />
                  </Tooltip>
                </Box>
              )}
            </Flex>
            <Field mt={2} mb={2} name="alertKey" onChange={this.onAlertKeyChange}>
              <InputGroup />
            </Field>
            {(inferredFields.includes('srcPorts') || inferredFields.includes('FS_MATCH_TYPE_SRC_PORT')) && (
              <Field name="srcPort">
                <InputGroup type="number" />
              </Field>
            )}
            {(inferredFields.includes('dstPorts') || inferredFields.includes('FS_MATCH_TYPE_DST_PORT')) && (
              <Field name="dstPort">
                <InputGroup type="number" />
              </Field>
            )}
            <Field mb={2} name="comment">
              <InputGroup />
            </Field>
            <Flex>
              <Field flex={1} mb={2} name="uiProperties.autoStopValue">
                <InputGroup
                  type="number"
                  rightElement={
                    <Field mb={2} showLabel={false} name="uiProperties.autoStopFactor">
                      <Select menuWidth="auto" width="auto" />
                    </Field>
                  }
                />
              </Field>
            </Flex>
            {$mkp.tenants.size > 0 && (
              <Field name="tenantID" options={[{ value: '', label: 'None' }, ...$mkp.tenants.tenantAlertingOptions]}>
                <Select
                  fill
                  showFilter
                  tetherOptions={this.tetherOptions}
                  optionFormatter={this.tenantOptionFormatter}
                  valueRenderer={this.tenantValueFormatter}
                />
              </Field>
            )}
            {collection.hasFetched && (
              <Card p={1}>
                <ToggleCollapse
                  label={
                    collection.loading
                      ? 'Checking for matching mitigations...'
                      : `${collection.size > 0 ? collection.size : 'No'} matching mitigation${
                          collection.size === 1 ? '' : 's'
                        } found`
                  }
                  buttonProps={{
                    disabled: collection.loading,
                    mt: 0,
                    mb: 0,
                    intent: !collection.loading && collection.size > 0 ? 'primary' : undefined,
                    textAlign: 'left'
                  }}
                >
                  {(collection.loading || collection.size > 0) && (
                    <Flex height={`${Math.min(collection.size, 5) * 42 + 43}px`} mt={1}>
                      <AdminTable
                        searchBar={false}
                        collection={collection}
                        fetchCollectionOnMount={false}
                        columns={this.columns}
                        kebabActions={kebabActions}
                        rowAlign="center"
                        hideResultCount
                      />
                    </Flex>
                  )}
                </ToggleCollapse>
              </Card>
            )}
          </Suspense>

          <Dialog
            isOpen={mitigationDetailsDialogOpen}
            title="Mitigation Method Details"
            canEscapeKeyClose
            canClickOutside
            isCloseButtonShown
            onClose={this.toggleMitigationDetailsDialog}
            width="400px"
          >
            <Dialog.Body>
              <FlowspecMethodSummary mitigation={selectedMethod?.method} useNameAsTitle />
            </Dialog.Body>
          </Dialog>

          {collection.size === 1 && (
            <Text as="div" mt={2} muted>
              Note: You cannot have two mitigations for the same IP/CIDR. Either retry the existing mitigation listed
              above or archive it to create another.
            </Text>
          )}
        </Flex>
      </FormDialog>
    );
  }
}
